import { sortBy } from 'lodash';

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object
    ? RecursivePartial<T[P]>
    : T[P];
};

export interface IApollo {
  __typename: string;
}

export interface ApplicationRecord extends IApollo {
  id: number;
  gid: string;
  updatedAt?: Date;
  createdAt?: Date;
}

export interface PageInfo {
  startCursor?: string;
  endCursor: string;
  hasNextPage: boolean;
  hasPreviousPage?: boolean;
}

export interface Nodes<T> {
  nodes: T[];
}

export interface ConnectionPagination<T> extends Nodes<T> {
  pageInfo: PageInfo;
}

// Append a value to an array with store.modify
export function append<T>(value: T) {
  return (values: T[], { toReference }) => {
    return [...values, toReference(value)];
  };
}

// Append a value to an array with store.modify, but sorted
export function appendSorted<T, K extends keyof T>(value: T, sortByProp: K) {
  return (values: T[], { toReference, readField }) => {
    return sortBy([...values, toReference(value)], (node) => {
      return readField(sortByProp, node);
    });
  };
}

// Append a value to a nodes array with store.modify
export function appendNode<T>(value: T) {
  return (connection: ConnectionPagination<T>, { toReference }) => {
    return { ...connection, nodes: [...connection.nodes, toReference(value)] };
  };
}

// Append a value to a nodes array with store.modify
export function appendSortedNode<T, K extends keyof T>(value: T, sortByProp: K) {
  return (connection: ConnectionPagination<T>, { toReference, readField }) => {
    return {
      ...connection,
      nodes: sortBy([...connection.nodes, toReference(value)], (node) => {
        return readField(sortByProp, node);
      })
    };
  };
}

// Append a value to a nodes array if it is a new value with store.modify
export function appendIfNew<T, U extends { __ref: string }>(value: T) {
  return (values: U[], { toReference }) => {
    const reference = toReference(value);
    if (values.find((element) => toReference(element).__ref == reference.__ref)) {
      return values;
    } else {
      return [...values, toReference(value)];
    }
  };
}

// Prepend a value to an array with store.modify
export function prepend<T>(value: T) {
  return (values: T[], { toReference }) => {
    return [toReference(value), ...values];
  };
}

// Prepend a value to a nodes array with store.modify
export function prependNode<T>(value: T) {
  return (connection: ConnectionPagination<T>, { toReference }) => {
    return { ...connection, nodes: [toReference(value), ...connection.nodes] };
  };
}

// Remove an item given it's id from an array of items
export function removeById<T>(id: number) {
  return (values: T[], { readField }) => {
    return values.filter((value) => readField('id', value) !== id);
  };
}

// Remove an item given it's GID from an array of items
export function removeByGid<T>(gid: string) {
  return (values: T[], { readField }) => {
    return values.filter((value) => readField('gid', value) !== gid);
  };
}

// Remove an item given it's GID from a nodes array
export function removeNodeByGid<T>(gid: string) {
  return (connection: ConnectionPagination<T>, { readField }) => {
    return {
      ...connection,
      nodes: connection.nodes.filter((value) => readField('gid', value) !== gid)
    };
  };
}

// Delete a property of a cached object
export function deleteProperty() {
  return (_value, { DELETE }) => {
    return DELETE;
  };
}

// Replace a property with the given value
export function replace<T>(value: T) {
  return (_existing: T) => {
    return value;
  };
}
